import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input, Label } from '../common';
import Dropdown from '../common/Dropdown';
import { fieldValidators } from '../../utils/validationSchemas';

/**
 * Organization detail form with comprehensive validation
 * Senior Developer Implementation:
 * - Real-time field validation
 * - Clear error messages
 * - Type-safe validation
 * - User-friendly feedback
 */
export function OrganizationForm({
  industryOptions,
  onSubmit,
  initialData,
  showSubmitButton = false,
}) {
  const [formData, setFormData] = useState(() => ({
    organizationName: initialData?.organizationName ?? '',
    industry: initialData?.industry ?? '',
    street: initialData?.street ?? '',
    city: initialData?.city ?? '',
    state: initialData?.state ?? '',
    zipCode: initialData?.zipCode ?? '',
  }));

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Clear errors on mount to ensure no errors are shown initially
  useEffect(() => {
    setErrors({});
    setTouched({});
  }, []);

  // Validate field on change (after it's been touched)
  const handleInputChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);

    // Auto-uppercase state field
    if (field === 'state' && value) {
      updated.state = value.toUpperCase().slice(0, 2);
    }

    // Validate if field has been touched
    if (touched[field]) {
      validateField(field, updated[field], updated);
    }

    // Update parent component in real-time
    onSubmit(updated);
  };

  // Validate single field
  const validateField = (field, value, allData = formData) => {
    let error = null;

    if (field === 'industry') {
      error = fieldValidators.industry(value, industryOptions);
    } else if (fieldValidators[field]) {
      error = fieldValidators[field](value);
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  // Handle blur event - mark field as touched and validate
  const handleBlur = (field) => {
    setHasInteracted(true);
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Validate all fields (only called when user tries to submit)
  const validateAll = () => {
    const newTouched = {};
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach((field) => {
      newTouched[field] = true;
      let error = null;

      if (field === 'industry') {
        error = fieldValidators.industry(formData[field], industryOptions);
      } else if (fieldValidators[field]) {
        error = fieldValidators[field](formData[field]);
      }

      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);
    setHasInteracted(true); // Mark as interacted when validateAll is called
    return isValid;
  };

  // Mark component as mounted after first render
  useEffect(() => {
    setIsMounted(true);
    // Initial call to parent with empty errors
    if (onSubmit) {
      onSubmit(formData, {
        validateAll,
        isValid: undefined,
        errors: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose validation function to parent (only when formData changes, not on mount)
  useEffect(() => {
    // Skip first render to prevent validation on mount
    if (!isMounted) return;

    if (onSubmit) {
      if (hasInteracted) {
        // Only calculate isValid if at least one field has been touched
        const isValid =
          Object.keys(touched).length > 0 &&
          Object.keys(formData).every((field) => {
            if (field === 'industry') {
              return !fieldValidators.industry(formData[field], industryOptions);
            }
            return !fieldValidators[field] || !fieldValidators[field](formData[field]);
          });

        onSubmit(formData, {
          validateAll,
          isValid: Object.keys(touched).length === 0 ? undefined : isValid,
          errors,
        });
      } else {
        // Before interaction, just pass the data without validation
        onSubmit(formData, {
          validateAll,
          isValid: undefined,
          errors: {},
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, hasInteracted, touched, errors, isMounted]);

  return (
    <div className="w-full">
      <div className="space-y-6">
        {/* Section Title */}
        <h2 className="font-['Roboto:Semibold',sans-serif] font-semibold text-[20px] text-black tracking-[0.1px] leading-tight">
          Organization Details
        </h2>

        {/* Row 1: Organization Name and Industry */}
        <div className="flex gap-[35px]">
          <div className="w-[305px]">
            <div className="form-group">
              <Label variant="gray" isRequired={true}>
                Organization Name
              </Label>
              <Input
                variant="primary"
                customClass={`!max-w-full ${errors.organizationName ? 'border-red-500' : ''}`}
                placeholder="e.g., Memorial Healthcare System"
                value={formData.organizationName}
                onChange={(e) => handleInputChange('organizationName', e.target.value)}
                onBlur={() => handleBlur('organizationName')}
              />
              {errors.organizationName && touched.organizationName && (
                <p className="text-red-500 text-xs mt-1">{errors.organizationName}</p>
              )}
            </div>
          </div>
          <div className="w-[305px]">
            <div className="form-group">
              <Label variant="gray" isRequired={true}>
                Industry
              </Label>
              <Dropdown
                options={industryOptions}
                selectedOption={
                  industryOptions.find((opt) => opt.value === formData.industry)?.label
                }
                onSelect={(option) => {
                  setHasInteracted(true);
                  handleInputChange('industry', option.value);
                  setTouched((prev) => ({ ...prev, industry: true }));
                }}
                text="Choose option"
                widthClass="w-full"
                onDropdownToggle={() => {}}
              />
              {errors.industry && touched.industry && (
                <p className="text-red-500 text-xs mt-1">{errors.industry}</p>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Street (Full Width) */}
        <div className="w-full">
          <div className="form-group">
            <Label variant="gray" isRequired={true}>
              Street
            </Label>
            <Input
              variant="primary"
              customClass={`!max-w-full ${errors.street ? 'border-red-500' : ''}`}
              placeholder="e.g., 123 Oak Avenue, Suite 200"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              onBlur={() => handleBlur('street')}
            />
            {errors.street && touched.street && (
              <p className="text-red-500 text-xs mt-1">{errors.street}</p>
            )}
          </div>
        </div>

        {/* Row 3: City, State, Zip Code */}
        <div className="flex gap-[35px]">
          <div className="w-[192px]">
            <div className="form-group">
              <Label variant="gray" isRequired={true}>
                City
              </Label>
              <Input
                variant="primary"
                customClass={`!max-w-full ${errors.city ? 'border-red-500' : ''}`}
                placeholder="e.g., Dallas"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                onBlur={() => handleBlur('city')}
              />
              {errors.city && touched.city && (
                <p className="text-red-500 text-xs mt-1">{errors.city}</p>
              )}
            </div>
          </div>
          <div className="w-[191px]">
            <div className="form-group">
              <Label variant="gray" isRequired={true}>
                State
              </Label>
              <Input
                variant="primary"
                customClass={`!max-w-full ${errors.state ? 'border-red-500' : ''}`}
                placeholder="e.g., TX"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                onBlur={() => handleBlur('state')}
                maxLength={2}
              />
              {errors.state && touched.state && (
                <p className="text-red-500 text-xs mt-1">{errors.state}</p>
              )}
            </div>
          </div>
          <div className="w-[192px]">
            <div className="form-group">
              <Label variant="gray" isRequired={true}>
                Zip Code
              </Label>
              <Input
                variant="primary"
                customClass={`!max-w-full ${errors.zipCode ? 'border-red-500' : ''}`}
                placeholder="e.g., 75201"
                value={formData.zipCode}
                onChange={(e) => {
                  // Only allow digits and hyphen
                  const value = e.target.value.replace(/[^\d-]/g, '');
                  handleInputChange('zipCode', value);
                }}
                onBlur={() => handleBlur('zipCode')}
                maxLength={10}
              />
              {errors.zipCode && touched.zipCode && (
                <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>
              )}
            </div>
          </div>
        </div>

        {/* Nota: isFormValid no se usa en este componente actualmente.
            Si showSubmitButton se usa en otra versión, acá podrías renderizar el botón. */}
      </div>
    </div>
  );
}

OrganizationForm.propTypes = {
  industryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  showSubmitButton: PropTypes.bool,
};
